import React, { useEffect, useState, useContext } from 'react';
import { Modal, Row, Col, Card, Spin, Typography, Divider, DatePicker, Tooltip } from "antd";
import { GetInteraccionsAyuda } from "../../../dao/GetInteraccionsAyuda";
import { GetAulaStats } from "../../../dao/GetAulaStats";
import { AuthContext } from '../../../config/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface AulaStatsModalProps {
    visible: boolean;
    onClose: () => void;
    aula: any;
}

const AulaStatsModal: React.FC<AulaStatsModalProps> = ({ visible, onClose, aula }) => {
    const { userId } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState<any>({
        total: 0,
        activas: 0,
        finalizadas: 0,
        usuariosUnicos: 0,
        mediaPorUsuario: 0,
        porFecha: []
    });

    const [extendedStats, setExtendedStats] = useState<any>({
        totalChats: 0,
        chatsMoreThanOneAnswer: 0,
        ratedChats: 0,
        positiveChats: 0,
        neutralChats: 0,
        negativeChats: 0,
        studentsOnce: 0,
        studentsTwoOrMore: 0,
        totalUsers: 0
    });

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
        dayjs().subtract(1, 'month'),
        dayjs()
    ]);

    useEffect(() => {
        if (visible && aula) {
            checkDades();
        }
    }, [visible, aula, dateRange]);

    async function checkDades() {
        setLoading(true);
        try {
            const dataIniStr = dateRange[0] ? dateRange[0].format('YYYY-MM-DD HH:mm:ss') : null;
            const dataFiStr = dateRange[1] ? dateRange[1].format('YYYY-MM-DD HH:mm:ss') : null;

            // 1. Get new extended stats from SQL procedure
            let sqlStatsRaw = await GetAulaStats(userId, aula?.id || 0, dataIniStr, dataFiStr);
            if (sqlStatsRaw) {
                let parsed = sqlStatsRaw;
                if (typeof sqlStatsRaw === 'string') {
                    try { parsed = JSON.parse(sqlStatsRaw); } catch(e){}
                } else if (sqlStatsRaw[0] && sqlStatsRaw[0].json) {
                    parsed = typeof sqlStatsRaw[0].json === 'string' ? JSON.parse(sqlStatsRaw[0].json) : sqlStatsRaw[0].json;
                } else if (sqlStatsRaw.json) {
                    parsed = typeof sqlStatsRaw.json === 'string' ? JSON.parse(sqlStatsRaw.json) : sqlStatsRaw.json;
                }
                setExtendedStats({ ...extendedStats, ...parsed });
            }

            // 2. Get previous data for graph and calculate basic numbers locally filtered by date
            const data = await GetInteraccionsAyuda(userId, aula?.id || 0);
            
            let filteredData = [];
            if (Array.isArray(data)) {
                filteredData = data.filter((item: any) => {
                    if (!item.moment) return false;
                    const mom = dayjs(item.moment);
                    if (dateRange[0] && mom.isBefore(dateRange[0])) return false;
                    if (dateRange[1] && mom.isAfter(dateRange[1])) return false;
                    return true;
                });
            }

            let total = filteredData.length;
            let activas = 0;
            let finalizadas = 0;
            const usuariosSet = new Set<string>();
            const fechasMap = new Map<string, { count: number; answersCount: number; chats: any[] }>();

            filteredData.forEach((item: any) => {
                if (item.estat === 0) activas++;
                else finalizadas++;

                if (item.id_user) usuariosSet.add(item.id_user);

                if (item.moment) {
                    const dateStr = dayjs(item.moment).format('DD/MM/YYYY');
                    const numAnswers = item.answers ? item.answers.length : 0;

                    const dayData = fechasMap.get(dateStr) || { count: 0, answersCount: 0, chats: [] };
                    dayData.count += 1;
                    dayData.answersCount += numAnswers;
                    dayData.chats.push({
                        id: item.id,
                        answersCount: numAnswers
                    });

                    fechasMap.set(dateStr, dayData);
                }
            });

            const usuariosUnicos = usuariosSet.size;
            const mediaPorUsuario = usuariosUnicos > 0 ? (total / usuariosUnicos).toFixed(1) : 0;

            const porFechaArray = Array.from(fechasMap.entries()).map(([fecha, data]) => ({
                fecha,
                count: data.count,
                answersCount: data.answersCount,
                chats: data.chats
            })).sort((a: any, b: any) => {
                const dateA = dayjs(a.fecha, 'DD/MM/YYYY').valueOf();
                const dateB = dayjs(b.fecha, 'DD/MM/YYYY').valueOf();
                return dateA - dateB;
            });

            setStats({
                total,
                activas,
                finalizadas,
                usuariosUnicos,
                mediaPorUsuario,
                porFecha: porFechaArray
            });
        } catch (error) {
            console.error("Error loading stats:", error);
        }
        setLoading(false);
    }

    const maxDateCount = stats.porFecha.reduce((max: number, item: any) => Math.max(max, item.count, item.answersCount), 0);

    return (
        <Modal
            title={<div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>📊 Estadístiques: {aula?.nom}</div>}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            centered
        >
            <Spin spinning={loading}>
                <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Text strong>Filtrar per dates:</Text>
                    <RangePicker 
                        showTime 
                        value={dateRange as any} 
                        onChange={(dates: any) => setDateRange(dates || [null, null])} 
                        style={{ width: '400px' }}
                    />
                </div>

                <div style={{ padding: '10px 0' }}>
                    <Title level={5}>Interaccions Globals</Title>
                    <Row gutter={[16, 16]}>
                        <Col span={6}>
                            <Card style={{ textAlign: 'center', backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1890ff' }}>{extendedStats.totalChats || 0}</div>
                                <div style={{ color: '#595959', fontWeight: '500' }}>Xats Totals</div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card style={{ textAlign: 'center', backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#faad14' }}>{extendedStats.chatsMoreThanOneAnswer || 0}</div>
                                <div style={{ color: '#595959', fontWeight: '500' }}>{"> 1 Resposta"}</div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card style={{ textAlign: 'center', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#52c41a' }}>{extendedStats.totalUsers || 0}</div>
                                <div style={{ color: '#595959', fontWeight: '500' }}>Alumnes Participats</div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card style={{ textAlign: 'center', backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
                                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#722ed1' }}>{extendedStats.ratedChats || 0}</div>
                                <div style={{ color: '#595959', fontWeight: '500' }}>Valoracions</div>
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    <Title level={5}>Comportament Alumnat i Ratings</Title>
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title="Ús per Alumne" size="small" bordered={false} style={{ background: '#fafafa' }}>
                                <Row justify="space-between" style={{ marginBottom: 8 }}>
                                    <Text>Només un xat:</Text>
                                    <Text strong>{extendedStats.studentsOnce || 0}</Text>
                                </Row>
                                <Row justify="space-between">
                                    <Text>Dos o més xats:</Text>
                                    <Text strong>{extendedStats.studentsTwoOrMore || 0}</Text>
                                </Row>
                            </Card>
                        </Col>
                        <Col span={16}>
                            <Card title="Valoracions Rebudes" size="small" bordered={false} style={{ background: '#fafafa' }}>
                                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                                    <Text>✅ Positives (Like):</Text>
                                    <Text strong style={{ color: '#52c41a' }}>{extendedStats.positiveChats || 0}</Text>
                                </Row>
                                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                                    <Text>➖ Neutrals:</Text>
                                    <Text strong style={{ color: '#faad14' }}>{extendedStats.neutralChats || 0}</Text>
                                </Row>
                                <Row justify="space-between" align="middle">
                                    <Text>❌ Negatives (Dislike):</Text>
                                    <Text strong style={{ color: '#ff4d4f' }}>{extendedStats.negativeChats || 0}</Text>
                                </Row>
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    <Title level={5} style={{ marginBottom: '16px' }}>Xats per Dia</Title>
                    {stats.porFecha.length > 0 ? (
                        <div style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '10px' }}>
                            {stats.porFecha.map((item: any, index: number) => {
                                const chatsWidth = maxDateCount > 0 ? (item.count / maxDateCount) * 100 : 0;
                                const answersWidth = maxDateCount > 0 ? (item.answersCount / maxDateCount) * 100 : 0;
                                
                                const tooltipContent = (
                                    <div style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Xats de {item.fecha}:</div>
                                        {item.chats.map((c: any, i: number) => (
                                            <div key={i} style={{ fontSize: '12px', borderBottom: '1px solid #ffffff40', paddingBottom: '4px', marginBottom: '4px' }}>
                                                Xat #{c.id}: <b>{c.answersCount}</b> respostes
                                            </div>
                                        ))}
                                    </div>
                                );

                                return (
                                    <Tooltip key={index} title={tooltipContent} placement="left">
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', cursor: 'pointer' }}>
                                            <div style={{ width: '90px', fontSize: '13px', color: '#555', fontWeight: '500' }}>
                                                {item.fecha}
                                            </div>
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', height: '14px' }}>
                                                    <div style={{ width: `${chatsWidth}%`, backgroundColor: '#1890ff', height: '100%', borderRadius: '4px' }}></div>
                                                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1890ff', fontWeight: '600', whiteSpace: 'nowrap' }}>{item.count} Xats</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', height: '14px' }}>
                                                    <div style={{ width: `${answersWidth}%`, backgroundColor: '#52c41a', height: '100%', borderRadius: '4px' }}></div>
                                                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#52c41a', fontWeight: '600', whiteSpace: 'nowrap' }}>{item.answersCount} Respostes</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    ) : (
                        <Text type="secondary">No hi ha dades disponibles per mostrar la gràfica en aquest rang de dates.</Text>
                    )}
                </div>
            </Spin>
        </Modal>
    );
};

export default AulaStatsModal;
